import { Lesson } from '../types';

const DRAFT_STORAGE_KEY = 'lesson_draft';
const DRAFT_TIMESTAMP_KEY = 'lesson_draft_timestamp';

export interface DraftMetadata {
  timestamp: number;
  title?: string;
}

/**
 * Sauvegarde un brouillon de leçon dans localStorage
 */
export const saveDraft = (lesson: Partial<Lesson>): void => {
  try {
    const draftData = {
      lesson,
      timestamp: Date.now()
    };
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
    localStorage.setItem(DRAFT_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('Error saving draft to localStorage:', error);
    // Si localStorage est plein, on peut essayer de nettoyer les anciens brouillons
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      localStorage.removeItem(DRAFT_TIMESTAMP_KEY);
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ lesson, timestamp: Date.now() }));
      localStorage.setItem(DRAFT_TIMESTAMP_KEY, Date.now().toString());
    } catch (retryError) {
      console.error('Error retrying draft save:', retryError);
    }
  }
};

/**
 * Charge un brouillon de leçon depuis localStorage
 */
export const loadDraft = (): Partial<Lesson> | null => {
  try {
    const draftDataStr = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!draftDataStr) {
      return null;
    }
    
    const draftData = JSON.parse(draftDataStr);
    return draftData.lesson || null;
  } catch (error) {
    console.error('Error loading draft from localStorage:', error);
    return null;
  }
};

/**
 * Récupère les métadonnées du brouillon (timestamp, titre, etc.)
 */
export const getDraftMetadata = (): DraftMetadata | null => {
  try {
    const timestampStr = localStorage.getItem(DRAFT_TIMESTAMP_KEY);
    const draftDataStr = localStorage.getItem(DRAFT_STORAGE_KEY);
    
    if (!timestampStr || !draftDataStr) {
      return null;
    }
    
    const draftData = JSON.parse(draftDataStr);
    const timestamp = parseInt(timestampStr, 10);
    
    return {
      timestamp,
      title: draftData.lesson?.metadata?.title
    };
  } catch (error) {
    console.error('Error getting draft metadata:', error);
    return null;
  }
};

/**
 * Supprime le brouillon de localStorage
 */
export const clearDraft = (): void => {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    localStorage.removeItem(DRAFT_TIMESTAMP_KEY);
  } catch (error) {
    console.error('Error clearing draft from localStorage:', error);
  }
};

/**
 * Vérifie si un brouillon existe
 */
export const hasDraft = (): boolean => {
  return localStorage.getItem(DRAFT_STORAGE_KEY) !== null;
};

/**
 * Formate la date du brouillon pour l'affichage
 */
export const formatDraftDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'À l\'instant';
  } else if (diffMins < 60) {
    return `Il y a ${diffMins} minute${diffMins > 1 ? 's' : ''}`;
  } else if (diffHours < 24) {
    return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
  } else if (diffDays === 1) {
    return 'Hier';
  } else if (diffDays < 7) {
    return `Il y a ${diffDays} jours`;
  } else {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

